// Control group (smart communication S1). Stored on the cadence as `settings.holdout_percent`: a whole
// number from 1 to 50, the share of NEW enrollments kept as a control group. They walk the workflow but
// every message step is skipped (a `skipped` event with reason `holdout`) and they get no job alerts.
// No key means no control group: the API rejects 0, so 0 or an empty field removes the key.

export const HOLDOUT_KEY = 'holdout_percent'
export const HOLDOUT_MAX_PERCENT = 50
export const HOLDOUT_REASON = 'holdout'

export const HOLDOUT_HELP =
	'Keep this share of new candidates as a control group: they go through the workflow but are sent nothing, so you can see how much the messages really help. 10% is a good start. Changing it only affects candidates who enrol afterwards.'
export const HOLDOUT_INVALID = `Enter a whole number from 0 to ${HOLDOUT_MAX_PERCENT}.`
export const HOLDOUT_NONE = 'None'

const isBlank = (value) => value === undefined || value === null || String(value).trim() === ''

// The stored value as typed (a number, or what an older writer left), or null when there is no control group.
export const readHoldoutPercent = (settings) => {
	const value = settings && typeof settings === 'object' ? settings[HOLDOUT_KEY] : undefined
	return isBlank(value) ? null : value
}

// Whole number from 1 to 50, or null for anything else.
export const holdoutPercentValue = (value) => {
	if (isBlank(value) || typeof value === 'boolean') return null
	const number = Number(value)
	return Number.isInteger(number) && number >= 1 && number <= HOLDOUT_MAX_PERCENT ? number : null
}

// The first problem the API would report (Cadence#typed_settings_are_well_formed), or null.
export const holdoutValidationMessage = (settings) => {
	const value = readHoldoutPercent(settings)
	if (value === null) return null
	return holdoutPercentValue(value) === null ? HOLDOUT_INVALID : null
}

// Merge, never replace. Blank or 0 removes the key; a valid percent is stored as an integer; anything else is
// kept as typed so the card can show it back and a save refuses it (saveProblem) instead of dropping it.
export const setHoldoutPercent = (settings, value) => {
	const rest = { ...(settings || {}) }
	delete rest[HOLDOUT_KEY]
	if (isBlank(value) || Number(value) === 0) return rest
	const percent = holdoutPercentValue(value)
	return { ...rest, [HOLDOUT_KEY]: percent ?? (Number.isFinite(Number(value)) ? Number(value) : value) }
}

// "10%" or "None": the card's header and the review-changes line.
export const describeHoldout = (value) => {
	if (isBlank(value)) return HOLDOUT_NONE
	const percent = holdoutPercentValue(value)
	return percent === null ? `${value}% (invalid)` : `${percent}%`
}
